import {
  Flex,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingInfo } from '@/types/billing';
import BillingInfoForm from './BillingInfoForm';

type BillingInfoModalProps = {
  onClose: () => void;
  billingInfo?: BillingInfo;
};

export const BillingInfoModal = ({
  onClose,
  billingInfo,
}: BillingInfoModalProps) => {
  const { t } = useTranslation('billing');
  // The handler is set in the child component, and is called when the user clicks the save button
  const submitHandlerRef = useRef<null | (() => void)>(null);

  // Use state for UI-related values that need to trigger re-renders
  const [isSubmitReady, setIsSubmitReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const setHandleSubmit = useCallback((handler: null | (() => void)) => {
    submitHandlerRef.current = handler;
  }, []);

  return (
    <ModalV2
      ariaLabelledby="billing-info-modal-header-text"
      hide={onClose}
      maxWidth="80rem"
      visible>
      <ModalHeader>
        <Flex vertical rowGap="2rem">
          <Text id="billing-info-modal-header-text" variant="heading-2">
            {billingInfo
              ? t('billingInfoModal.title.edit')
              : t('billingInfoModal.title.add')}
          </Text>
        </Flex>
      </ModalHeader>
      <ModalBody>
        <BillingInfoForm
          onClose={onClose}
          billingInfo={billingInfo}
          setHandleSubmit={setHandleSubmit}
          setIsSubmitReady={setIsSubmitReady}
          setIsLoading={setIsLoading}
        />
      </ModalBody>
      <ModalFooter>
        <ModalFooterActions>
          <ModalAction
            id="billing-info-modal-cancel-button"
            variant="text"
            disabled={isLoading}
            onClick={onClose}>
            {t('billingInfoModal.buttons.cancel')}
          </ModalAction>
          <ModalAction
            id="billing-info-modal-save-button"
            loading={isLoading}
            disabled={!isSubmitReady}
            onClick={() => {
              if (submitHandlerRef.current) {
                submitHandlerRef.current();
              }
            }}
            type="submit">
            {t('billingInfoModal.buttons.save')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};
